import { Box } from '@mentimeter/ragnar-ui/box';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import type { PinOnImageResult } from '@core-api/presentation/types/response';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { useImageSize } from './utils/use-image-size';
import { PinOnImageVisualization } from './PinOnImageVisualization';
import { ScreenReaderResults } from './ScreenReaderResults';

interface PinOnImagePresentationProps {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  results: PinOnImageResult[];
  imageUrl: string | undefined;
  showCorrectAnswerState: 'disabled' | 'enabled-visible' | 'enabled-hidden';
  correctArea: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } | null;
  shouldAnimate: boolean;
  shouldHideResults: boolean;
}

export const PinOnImagePresentation = ({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  results: rawResults,
  imageUrl,
  showCorrectAnswerState,
  correctArea,
  shouldAnimate,
  CanvasVisualizationArea,
  shouldHideResults,
}: PinOnImagePresentationProps) => {
  const imageSize = useImageSize(imageUrl);

  const ConditionalWrapper = CanvasVisualizationArea ?? Box;

  const results = shouldHideResults ? [] : rawResults;

  return (
    <Box width="100%" height="100%" alignItems="center" justifyContent="center">
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />

      <ConditionalWrapper flex="1" width="100%">
        <PinOnImageVisualization
          imageUrl={imageUrl}
          imageSize={imageSize}
          results={results}
          disableAnimations={!shouldAnimate}
          correctArea={
            showCorrectAnswerState !== 'disabled' && correctArea
              ? {
                  type: showCorrectAnswerState,
                  correctArea: {
                    top: correctArea.top,
                    bottom: correctArea.bottom,
                    left: correctArea.left,
                    right: correctArea.right,
                  },
                }
              : { type: 'disabled' }
          }
        />
        <ScreenReaderOnly>
          <ScreenReaderResults
            results={results}
            shouldHideResults={shouldHideResults}
            shouldShowCorrect={showCorrectAnswerState === 'enabled-visible'}
          />
        </ScreenReaderOnly>
      </ConditionalWrapper>
    </Box>
  );
};
